import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser, loginWithGoogle, clearAuthError } from "../redux/action/actions";

const GOOGLE_LOGIN_URL = "http://localhost:8000/auth/login"; // Đổi lại nếu backend chạy port khác

const Login = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [glitchActive, setGlitchActive] = useState(false);
  const [loginMode, setLoginMode] = useState('admin'); // 'admin' or 'google'
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, error } = useSelector(state => state.auth);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Trigger glitch effect randomly
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 300);
    }, 5000);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(glitchInterval);
    };
  }, []);

  // Clear errors when component unmounts or mode changes
  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch, loginMode]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await dispatch(loginUser(formData.username, formData.password));
      // Navigation will happen via useEffect when isAuthenticated changes
    } catch (err) {
      // Error will be handled by Redux
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = GOOGLE_LOGIN_URL;
  };

  const toggleLoginMode = () => {
    setLoginMode(loginMode === 'admin' ? 'google' : 'admin');
    setFormData({ username: '', password: '' });
    dispatch(clearAuthError());
  };

  return (
    <div className="min-h-screen bg-black overflow-hidden relative flex items-center justify-center">
      {/* Cyberpunk Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(cyan 1px, transparent 1px),
            linear-gradient(90deg, cyan 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridPulse 4s ease-in-out infinite'
        }}></div>
      </div>

      {/* Floating Neon Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400 rounded-full shadow-neon-cyan animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
        {[...Array(10)].map((_, i) => (
          <div
            key={`pink-${i}`}
            className="absolute w-1 h-1 bg-pink-500 rounded-full shadow-neon-pink animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Dynamic Mouse Cursor Effect */}
      <div 
        className="absolute w-32 h-32 pointer-events-none z-10 opacity-30 rounded-full"
        style={{
          left: mousePosition.x - 64,
          top: mousePosition.y - 64,
          background: 'radial-gradient(circle, rgba(0,255,255,0.3) 0%, rgba(255,0,255,0.2) 50%, transparent 70%)',
          transition: 'all 0.1s ease-out'
        }}
      />

      {/* Holographic Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-full blur-3xl animate-pulse-neon"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-neon delay-1000"></div>
      </div>

      {/* Scanlines Effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent animate-scanlines"></div>
      </div>

      {/* Login Container */}
      <div className="relative z-20 w-full max-w-md mx-4">
        {/* Login Card */}
        <div className="group relative bg-black/80 backdrop-blur-md border border-cyan-400/50 rounded-2xl p-8 shadow-neon-cyan hover:shadow-neon-cyan-intense transition-all duration-500 overflow-hidden">
          {/* Animated Border */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-pink-500/20 to-purple-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-slide-right rounded-t-2xl"></div>
          
          {/* Content */}
          <div className="relative z-10">
            {/* Title with Glitch Effect */}
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-black font-mono mb-2 ${glitchActive ? 'animate-glitch' : ''}`}>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-purple-500 animate-gradient-x">
                  AUTH PROTOCOL
                </span>
              </h1>
              <p className="text-gray-400 font-mono text-sm">
                {'>'} Initializing secure connection<span className="animate-blink text-green-400">█</span>
              </p>
            </div>

            {/* Login Mode Toggle */}
            <div className="mb-6 flex bg-black/60 border border-cyan-400/30 rounded-lg p-1">
              <button
                onClick={() => setLoginMode('admin')}
                className={`flex-1 py-2 px-4 rounded-md font-mono text-sm transition-all duration-300 ${
                  loginMode === 'admin'
                    ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/50'
                    : 'text-gray-400 hover:text-cyan-300'
                }`}
              >
                ADMIN ACCESS
              </button>
              <button
                onClick={() => setLoginMode('google')}
                className={`flex-1 py-2 px-4 rounded-md font-mono text-sm transition-all duration-300 ${
                  loginMode === 'google'
                    ? 'bg-pink-400/20 text-pink-300 border border-pink-400/50'
                    : 'text-gray-400 hover:text-pink-300'
                }`}
              >
                OAUTH LOGIN
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-3 bg-red-900/20 border border-red-400/50 rounded-lg">
                <div className="flex items-center gap-2 text-red-400 font-mono text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  [ERROR] {error}
                </div>
              </div>
            )}

            {/* Admin Login Form */}
            {loginMode === 'admin' && (
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div>
                  <label className="block text-cyan-300 font-mono text-sm mb-2">
                    USERNAME
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/60 border border-cyan-400/30 rounded-lg text-cyan-300 font-mono focus:outline-none focus:border-cyan-400 focus:shadow-neon-cyan transition-all duration-300 placeholder-gray-500"
                    placeholder="Enter username..."
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-cyan-300 font-mono text-sm mb-2">
                    PASSWORD
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/60 border border-cyan-400/30 rounded-lg text-cyan-300 font-mono focus:outline-none focus:border-cyan-400 focus:shadow-neon-cyan transition-all duration-300 placeholder-gray-500 pr-12"
                      placeholder="Enter password..."
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex items-center justify-center gap-3 bg-transparent border-2 border-cyan-500 text-cyan-500 font-bold py-4 px-6 rounded-xl shadow-neon-cyan hover:shadow-neon-cyan-intense transform hover:scale-105 transition-all duration-300 overflow-hidden font-mono disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                        AUTHENTICATING...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        ADMIN LOGIN
                      </>
                    )}
                  </span>
                  
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-cyan-500 rounded-xl transform -skew-x-12 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 opacity-20"></div>
                  <div className="absolute inset-0 border border-cyan-500 rounded-xl animate-border-pulse"></div>
                </button>
              </form>
            )}

            {/* Google Login */}
            {loginMode === 'google' && (
              <div className="space-y-4">
                <button
                  onClick={handleGoogleLogin}
                  className="group relative w-full flex items-center justify-center gap-3 bg-transparent border-2 border-pink-500 text-pink-500 font-bold py-4 px-6 rounded-xl shadow-neon-pink hover:shadow-neon-pink-intense transform hover:scale-105 transition-all duration-300 overflow-hidden font-mono"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    {/* Enhanced Google Icon with Cyberpunk Effect */}
                    <div className="relative">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="relative z-10 group-hover:scale-110 transition-transform duration-300"
                      >
                        <g>
                          <path
                            d="M43.6 20.5H42V20.5H24V27.5H35.1C33.5 31.4 29.1 34 24 34C17.4 34 12 28.6 12 22C12 15.4 17.4 10 24 10C27.1 10 29.9 11.1 32 13L37 8C33.5 4.8 28.9 3 24 3C12.9 3 4 11.9 4 23C4 34.1 12.9 43 24 43C34.6 43 43 34.6 43 24C43 22.7 42.9 21.6 42.6 20.5Z"
                            fill="currentColor"
                            className="text-pink-500"
                          />
                        </g>
                      </svg>
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-pink-500 rounded-full blur-md opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    GOOGLE AUTH INIT
                  </span>
                  
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-pink-500 rounded-xl transform -skew-x-12 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 opacity-20"></div>
                  <div className="absolute inset-0 border border-pink-500 rounded-xl animate-border-pulse"></div>
                  
                  {/* Loading Dots Animation */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-1 h-1 bg-pink-400 rounded-full animate-pulse"></div>
                    <div className="w-1 h-1 bg-pink-400 rounded-full animate-pulse delay-200"></div>
                    <div className="w-1 h-1 bg-pink-400 rounded-full animate-pulse delay-400"></div>
                  </div>
                </button>
              </div>
            )}

            {/* Security Status */}
            <div className="mt-6 p-3 bg-black/60 border border-green-400/30 rounded-lg">
              <div className="flex items-center gap-2 text-xs font-mono">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400">SECURE CONNECTION</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono mt-1">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse delay-500"></div>
                <span className="text-cyan-400">
                  {loginMode === 'admin' ? 'ADMIN READY' : 'OAUTH READY'}
                </span>
              </div>
            </div>

            {/* System Info */}
            <div className="mt-4 p-3 bg-black/40 border border-cyan-400/20 rounded-lg">
              <div className="text-xs font-mono text-gray-500 space-y-1">
                <div className="flex justify-between">
                  <span>PROTOCOL:</span>
                  <span className="text-cyan-400">
                    {loginMode === 'admin' ? 'ADMIN-AUTH' : 'OAuth 2.0'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>ENCRYPTION:</span>
                  <span className="text-green-400">AES-256</span>
                </div>
                <div className="flex justify-between">
                  <span>STATUS:</span>
                  <span className="text-pink-400 animate-pulse">
                    {isLoading ? 'PROCESSING' : 'STANDBY'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Warning */}
        <div className="mt-4 text-center">
          <p className="text-xs font-mono text-gray-600">
            {'>'} UNAUTHORIZED ACCESS WILL BE <span className="text-red-400">TERMINATED</span>
          </p>
          {loginMode === 'admin' && (
            <p className="text-xs font-mono text-cyan-400 mt-2">
              {'>'} Default: admin / admin123
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
